import { DestroyRef, effect, inject, Injectable, Injector, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  skipWhile,
  switchMap,
  tap,
  zip
} from 'rxjs';
import {
  IDocumentViewerSetting,
  IEncryption,
  IHomePageSetting,
  IStartPageSetting,
  IUserDocumentViewerSetting,
  TPageSetting
} from '@interfaces/nebula';
import { nebulaConst } from '@interfaces/nebulaConst';
import { SharedataService } from './sharedata.service';
import { ThemeService } from './theme.service';
import { ConfigurationService } from './configuration.service';
import { LoggerService } from './logger.service';
import { LoginService } from './login.service';
import { UserOnlineStatusService } from './user-online-status.service';
import { IUserSessionConfiguration, UserSessionsService } from './user-sessions.service';
import { AuditService } from './audit.service';

/**
 * Lädt je nach Anmeldestatus Hintergrund, Theme und die Benutzer-Konfiguration
 * und hält den aktuell angezeigten Hintergrund.
 */
@Injectable({
  providedIn: 'root'
})
export class UserContextLoaderService {
  private auditService = inject(AuditService);
  private configurationService = inject(ConfigurationService);
  private logger = inject(LoggerService);
  private sharedData = inject(SharedataService);
  private themeService = inject(ThemeService);
  private onlineStatusService = inject(UserOnlineStatusService);
  private loginService = inject(LoginService);
  private userSessionService = inject(UserSessionsService);
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  #displayedBackground = signal<string>('');

  /** Aktuell angezeigter Hintergrund (Startseite oder Benutzer) */
  readonly displayedBackground = this.#displayedBackground.asReadonly();

  /** Startet das Laden von Hintergrund, Theme und Benutzer-Konfiguration */
  start(): void {
    const isAuthenticated$ = this.loginService.isAuthenticated().pipe(shareReplay(1));

    // Benutzer-Stream auf den angemeldeten Benutzer (Vertretung ändert nichts): erst nach der Sitzungsprüfung,
    // bei bestehender Sitzung ohne das initiale null, nur bei Wechsel der Benutzer-ID
    const userId$ = isAuthenticated$.pipe(
      switchMap(isAuthenticated => this.loginService.logonUser$.pipe(skipWhile(user => isAuthenticated && !user))),
      map(user => user?.id ?? null),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    userId$
      .pipe(
        switchMap(userId => {
          if (!userId) {
            // ohne Benutzer: Startseiten-Konfiguration samt Hintergrund, danach Theme
            return this.configurationService.getJson(nebulaConst.startPageConfig, 'user').pipe(
              catchError(() => of(null)),
              tap(setting =>
                this.#displayedBackground.set(
                  (setting as IStartPageSetting)?.background?.backgroundSrc ||
                    '/assets/img/background-pic/space_office.jpg'
                )
              ),
              switchMap(() => this.getAppTheme()),
              catchError(() => EMPTY)
            );
          }
          // mit Benutzer: Hintergrund, danach Theme des Benutzers; parallel die übrige Benutzer-Konfiguration.
          // Hintergrund des vorherigen Benutzers verwerfen, damit nur der neue angezeigt wird.
          this.sharedData.isCustomBackground.set(false);
          this.sharedData.background.set('');
          // persönliche Dokumentansicht des vorherigen Benutzers verwerfen
          this.sharedData.userDocumentViewerMode.set('system');
          const backgroundAndTheme$ = this.themeService.getBackground().pipe(
            tap(imageUrl => {
              if (imageUrl) {
                this.sharedData.background.set(imageUrl);
              }
            }),
            switchMap(() => this.getAppTheme()),
            catchError(() => EMPTY)
          );
          return merge(backgroundAndTheme$, this.loadUserConfiguration());
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // Hintergrund des Benutzers anzeigen; ohne Benutzer setzt die Startseiten-Konfiguration ihn direkt
    effect(
      () => {
        const url = this.sharedData.background();
        if (url) {
          untracked(() => this.#displayedBackground.set(url));
        }
      },
      { injector: this.injector }
    );

    effect(
      () => {
        const theme = this.sharedData.theme();
        untracked(() => this.themeService.applyTheme(theme));
      },
      { injector: this.injector }
    );
  }

  private getAppTheme(): Observable<string> {
    return this.configurationService.getWebAppDesign('user').pipe(
      tap({
        next: theme => {
          if (theme) {
            this.sharedData.setTheme(theme);
          } else {
            this.sharedData.setTheme('');
          }
        }
      })
    );
  }

  /** Konfiguration, die nur für einen angemeldeten Benutzer geladen wird */
  private loadUserConfiguration(): Observable<unknown> {
    return zip(
      this.getHomePageSetting(),
      this.getDocumentViewerSetting(),
      this.getThemeGlobalOnly(),
      this.getLifeTimeSession(),
      this.getAuditConfiguration(),
      this.getEncryption(),
      this.onlineStatusService.getOnlineStatusTypes(),
      this.onlineStatusService.getOnlineStatusConfiguration(),
      this.logger.getLogLevel()
    ).pipe(catchError(() => EMPTY));
  }

  private getHomePageSetting(): Observable<TPageSetting> {
    return this.configurationService.getJson(nebulaConst.homePageConfig, 'global').pipe(
      tap(data => {
        if (data) {
          this.sharedData.homePageSetting.set(data as IHomePageSetting);
        }
      })
    );
  }

  /**
   * Globale und persönliche Einstellung „Dokumentansicht“. Ohne gültige globale Einstellung bleibt 'sidePanel',
   * ohne persönliche Wahl 'system'; Fehler werden einzeln abgefangen, damit zip die übrige Konfiguration nicht
   * verliert.
   */
  private getDocumentViewerSetting(): Observable<unknown> {
    const global$ = this.configurationService
      .getJson(nebulaConst.documentViewerConfig, 'global')
      .pipe(catchError(() => of(null)));
    const user$ = this.configurationService
      .getJson(nebulaConst.documentViewerConfig, 'user')
      .pipe(catchError(() => of(null)));
    return zip(global$, user$).pipe(
      tap(([globalData, userData]) => {
        const globalMode = (globalData as IDocumentViewerSetting)?.mode;
        if (globalMode === 'popup' || globalMode === 'sidePanel') {
          this.sharedData.globalDocumentViewerMode.set(globalMode);
        }
        // nur ein als persönlich gekennzeichneter Wert gilt als persönliche Wahl
        const userSetting = userData as IUserDocumentViewerSetting;
        const userMode = userSetting?.personal === true ? userSetting.mode : null;
        this.sharedData.userDocumentViewerMode.set(
          userMode === 'popup' || userMode === 'sidePanel' || userMode === 'system' ? userMode : 'system'
        );
      })
    );
  }

  private getThemeGlobalOnly(): Observable<boolean> {
    return this.configurationService.getWebAppDesignGlobalOnly().pipe(
      tap(globalOnly => {
        if (globalOnly !== null && globalOnly !== undefined) {
          this.sharedData.isThemeGlobalOnly.set(globalOnly);
        }
      })
    );
  }

  private getLifeTimeSession(): Observable<IUserSessionConfiguration> {
    return this.userSessionService
      .getUserSessionsConfiguration()
      .pipe(tap(response => this.sharedData.userSessionConfiguration.set(response)));
  }

  private getAuditConfiguration(): Observable<boolean> {
    return this.auditService.getConfiguration().pipe(
      map(setting => setting.isEnabled),
      tap(isEnabled => this.sharedData.isAuditEnabled.set(isEnabled))
    );
  }

  private getEncryption(): Observable<IEncryption> {
    return this.configurationService
      .getEncryption()
      .pipe(tap(encryption => this.sharedData.encryption.set(encryption)));
  }
}
